// The Forecasting tab's Support view (2026-10-28, Patrick's ask), with the weeks ahead (2026-11-02).
//   1. Volume    one weekly line, CXHub Support email plus the Bright Pattern CSS and TRQ
//                queues, with the split shown; Support's phone calls (TRQ Inbound Voice, and the
//                internal CS Support calls) beside it, not in the email total.
//   2. Staffing  handle time, the FTE the work asks for on his 23% method (CXHub email, Bright
//                Pattern email and phone apart), the Patrick team's scheduled hours as supply,
//                the Seniors' measured help and the gap before and after it. The weeks ahead
//                (italic) are the forecast against the schedule.
//   3. Service   Support Email's average time to first answer against its target.
// All the arithmetic is on the server (server/src/services/forecasting-support.js); this
// only lays it out. Hours sit behind a toggle, as on the other views.
import { useEffect, useState } from 'react'
import {
  Alert, Box, CircularProgress, FormControlLabel, Paper, Switch, Table, TableBody, TableHead, TableRow, Typography,
} from '@mui/material'
import { fetchForecastSupport } from '../api.js'
import SectionHeading from './SectionHeading.jsx'
import { Cell, Head, WeekCell, Fte, Divider, rowSx } from './ForecastTableParts.jsx'
import { formatCount, formatGap, formatSeconds, gapTone, weekLabel, EM_DASH } from '../forecastPhoneChat.js'
import { formatAvgHours, formatShare, slaHeader, slaTone } from '../forecastEmail.js'
import { MultiplierTable } from './ForecastEmail.jsx'

const TABLE_BOX = { width: 'fit-content', maxWidth: '100%' }

// Scheduled Bright Pattern queue hours of the Support teams: whole hours, a dash with no schedule.
const formatQueueHours = (h) => (typeof h === 'number' && Number.isFinite(h) ? `${Math.round(h)} h` : EM_DASH)

function VolumeTable({ report }) {
  const forecast = report.forecast?.weeks ?? []
  return (
    <Paper variant="outlined" sx={{ overflowX: 'auto', ...TABLE_BOX }}>
      <Table size="small" sx={{ width: 'auto', borderCollapse: 'collapse' }}>
        <TableHead>
          <TableRow>
            <Head align="left">Week ending</Head>
            <Head left>CXHub Support email</Head>
            <Head>Bright Pattern (CSS, TRQ)</Head>
            <Head left>Total</Head>
            <Head>Bright Pattern share</Head>
            <Head left>Emails handled (CXHub + Bright Pattern)</Head>
            <Head left>TRQ Inbound Voice calls</Head>
            <Head>CS Support calls (internal)</Head>
          </TableRow>
        </TableHead>
        <TableBody>
          <Divider colSpan={8}>Recent weeks · oldest first, then this week so far</Divider>
          {report.weeks.map((row) => {
            const v = row.volume
            const partial = row.isPartial
            return (
              <TableRow key={row.week} sx={rowSx(row)}>
                <WeekCell row={row} />
                <Cell left muted={v.cxhub == null || partial} italic={partial}>{formatCount(v.cxhub)}</Cell>
                <Cell muted={v.bp == null || partial} italic={partial}>{formatCount(v.bp)}</Cell>
                <Cell left bold muted={partial} italic={partial}>{formatCount(v.total)}{partial && v.total != null ? ' so far' : ''}</Cell>
                <Cell muted>{formatShare(v.bpShare)}</Cell>
                <Cell left muted={v.handledByPeople == null || partial} italic={partial}>{formatCount(v.handledByPeople)}</Cell>
                <Cell left muted={v.phone?.trqInbound == null || partial} italic={partial}>{formatCount(v.phone?.trqInbound)}</Cell>
                <Cell muted={v.phone?.internal == null || partial} italic={partial}>{formatCount(v.phone?.internal)}</Cell>
              </TableRow>
            )
          })}
          {forecast.length > 0 && (
            <>
              <Divider colSpan={8}>Forecast · this week in full, then the weeks ahead</Divider>
              {forecast.map((row) => {
                const v = row.volume
                return (
                  <TableRow key={row.week} sx={rowSx(row)}>
                    <WeekCell row={row} />
                    <Cell left italic muted={v.cxhub == null}>{formatCount(v.cxhub)}</Cell>
                    <Cell italic muted={v.bp == null}>{formatCount(v.bp)}</Cell>
                    <Cell left bold italic muted={v.total == null}>{formatCount(v.total)}</Cell>
                    <Cell italic muted>{EM_DASH}</Cell>
                    <Cell left italic muted={v.handledByPeople == null}>{formatCount(v.handledByPeople)}</Cell>
                    <Cell left italic muted={v.phone?.trqInbound == null}>{formatCount(v.phone?.trqInbound)}</Cell>
                    <Cell italic muted={v.phone?.internal == null}>{formatCount(v.phone?.internal)}</Cell>
                  </TableRow>
                )
              })}
            </>
          )}
        </TableBody>
      </Table>
    </Paper>
  )
}

function StaffingTable({ report, showHours }) {
  const a = report.assumptions
  const forecast = report.forecast?.weeks ?? []
  return (
    <Paper variant="outlined" sx={{ overflowX: 'auto', ...TABLE_BOX }}>
      <Table size="small" sx={{ width: 'auto', borderCollapse: 'collapse' }}>
        <TableHead>
          <TableRow>
            <Head rowSpan={2} align="left">Week ending</Head>
            <Head colSpan={5} left>Handle time</Head>
            <Head colSpan={4} left>FTE needed (× {1 + a.shrinkageRate})</Head>
            <Head rowSpan={2} left>FTE scheduled, {a.scheduleTeams.join(' and ')} (÷ {a.scheduledHoursPerFte})</Head>
            <Head rowSpan={2} left>Seniors' help</Head>
            <Head colSpan={2} left>Gap</Head>
            <Head colSpan={2} left>Memo, hours a week</Head>
          </TableRow>
          <TableRow>
            <Head left>CXHub</Head>
            <Head>Bright Pattern ÷ {a.bpInFocusDivisor}</Head>
            <Head>Email, all</Head>
            <Head>TRQ Inbound Voice</Head>
            <Head>CS Support</Head>
            <Head left>CXHub email</Head>
            <Head>Bright Pattern email</Head>
            <Head>Phone</Head>
            <Head>Total</Head>
            <Head left>Before help</Head>
            <Head>After help</Head>
            <Head left>Bright Pattern queue time scheduled</Head>
            <Head>CXHub work on 1A and AM</Head>
          </TableRow>
        </TableHead>
        <TableBody>
          <Divider colSpan={15}>Recent weeks · oldest first, then this week so far</Divider>
          {report.weeks.map((row) => {
            const p = row.plan
            const partial = row.isPartial
            return (
              <TableRow key={row.week} sx={rowSx(row)}>
                <WeekCell row={row} />
                <Cell left muted={row.aht.cxhubSec == null}>{formatSeconds(row.aht.cxhubSec)}</Cell>
                <Cell muted={row.aht.bpSec == null}>{formatSeconds(row.aht.bpSec)}</Cell>
                <Cell muted={row.aht.combinedSec == null}>{formatSeconds(row.aht.combinedSec)}</Cell>
                <Cell muted={row.aht.trqInboundSec == null}>{formatSeconds(row.aht.trqInboundSec)}</Cell>
                <Cell muted={row.aht.internalSec == null}>{formatSeconds(row.aht.internalSec)}</Cell>
                <Cell left muted={!p.needCxhub || partial} italic={partial}><Fte value={p.needCxhub} showHours={showHours} /></Cell>
                <Cell muted={!p.needBp || partial} italic={partial}><Fte value={p.needBp} showHours={showHours} /></Cell>
                <Cell muted={!p.needPhone || partial} italic={partial}><Fte value={p.needPhone} showHours={showHours} /></Cell>
                <Cell bold muted={!p.need || partial} italic={partial}><Fte value={p.need} showHours={showHours} /></Cell>
                <Cell left bold muted={!p.supply}><Fte value={p.supply} showHours={showHours} /></Cell>
                <Cell left muted={!p.help}><Fte value={p.help} showHours={showHours} /></Cell>
                <Cell left bold tone={gapTone(p.gapBefore)} muted={p.gapBefore == null}>{formatGap(p.gapBefore)}</Cell>
                <Cell bold tone={gapTone(p.gapAfter)} muted={p.gapAfter == null}>{formatGap(p.gapAfter)}</Cell>
                <Cell left muted>{formatQueueHours(p.bpQueueHours)}</Cell>
                <Cell muted>{formatQueueHours(p.elsewhereHours)}</Cell>
              </TableRow>
            )
          })}
          {forecast.length > 0 && (
            <>
              <Divider colSpan={15}>Forecast · this week in full, then the weeks ahead (schedules run through {report.scheduledThrough ? weekLabel(report.scheduledThrough) : EM_DASH})</Divider>
              {forecast.map((row) => {
                const p = row.plan
                return (
                  <TableRow key={row.week} sx={rowSx(row)}>
                    <WeekCell row={row} />
                    {[0, 1, 2, 3, 4].map((i) => <Cell key={i} left={i === 0} italic muted>{EM_DASH}</Cell>)}
                    <Cell left italic muted={!p.needCxhub}><Fte value={p.needCxhub} showHours={showHours} /></Cell>
                    <Cell italic muted={!p.needBp}><Fte value={p.needBp} showHours={showHours} /></Cell>
                    <Cell italic muted={!p.needPhone}><Fte value={p.needPhone} showHours={showHours} /></Cell>
                    <Cell bold italic muted={!p.need}><Fte value={p.need} showHours={showHours} /></Cell>
                    <Cell left bold italic muted={!p.supply}>
                      <Fte value={p.supply} showHours={showHours} />
                      {row.lowSchedule && <Typography component="span" variant="caption" color="warning.main" sx={{ ml: 0.5 }} title="Scheduled hours are well under recent weeks: time off, or shifts not entered yet">†</Typography>}
                    </Cell>
                    <Cell left italic muted>{EM_DASH}</Cell>
                    <Cell left bold italic tone={gapTone(p.gapBefore)} muted={p.gapBefore == null}>{formatGap(p.gapBefore)}</Cell>
                    <Cell italic muted>{EM_DASH}</Cell>
                    <Cell left italic muted>{EM_DASH}</Cell>
                    <Cell italic muted>{EM_DASH}</Cell>
                  </TableRow>
                )
              })}
            </>
          )}
        </TableBody>
      </Table>
    </Paper>
  )
}

function ServiceTable({ report }) {
  const rows = report.slaRows
  return (
    <Paper variant="outlined" sx={{ overflowX: 'auto', ...TABLE_BOX }}>
      <Table size="small" sx={{ width: 'auto', borderCollapse: 'collapse' }}>
        <TableHead>
          <TableRow>
            <Head align="left">Week ending</Head>
            {rows.map((r, i) => <Head key={r.key} left={i === 0}>{slaHeader(r)}</Head>)}
          </TableRow>
        </TableHead>
        <TableBody>
          <Divider colSpan={rows.length + 1}>Average time to first answer · recent weeks, oldest first</Divider>
          {report.weeks.map((week) => (
            <TableRow key={week.week} sx={rowSx(week)}>
              <WeekCell row={week} />
              {rows.map((r, i) => {
                const s = week.service[r.key]
                return (
                  <Cell key={r.key} left={i === 0} tone={slaTone(s?.avgHours, s?.targetHours)} muted={s?.avgHours == null} italic={week.isPartial}>
                    {s?.avgHours == null ? EM_DASH : formatAvgHours(s.avgHours)}
                  </Cell>
                )
              })}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Paper>
  )
}

// The loaded view, apart from the fetch so it can be rendered from a report alone.
export function ForecastSupportView({ report }) {
  const [showHours, setShowHours] = useState(false)
  const a = report.assumptions
  return (
    <Box>
      <SectionHeading variant="subtitle1" sx={{ mb: 1, ...TABLE_BOX }} title="Forecasting: Support" descriptionMaxWidth={1100}>
        What happened, week by week, oldest first, then this week in full and the weeks ahead as a forecast (italic). One volume line for the Support team's email: CXHub
        Support email (1A Support, 1A and AM Customer Support, and the Sales Hold callback contacts; spam out) plus the Bright Pattern CSS and
        TRQ queues. Some Bright Pattern Support queues have moved to CXHub (CSS Internal and the two Customer Support mailboxes, in October), so
        a little of the same work still shows on the Bright Pattern side; they are different messages, not counted twice.
      </SectionHeading>
      <FormControlLabel
        control={<Switch size="small" checked={showHours} onChange={(e) => setShowHours(e.target.checked)} />}
        label="Show hours under each FTE"
        sx={{ mb: 1 }}
      />
      {report.forecast?.baseline && (
        <Alert severity="info" sx={{ mb: 1, maxWidth: 1100 }}>
          The weeks ahead are the mean of the last {report.forecast.baseline.weeks.length} complete weeks' email work ({report.forecast.baseline.emailHours} h, each week taken out of its
          holiday multiplier) times the week's holiday multiplier, plus the phone work held flat ({report.forecast.baseline.phoneHours} h). The Seniors' help is not assumed ahead.
        </Alert>
      )}
      {report.warnings.includes('schedule_low') && (
        <Alert severity="warning" sx={{ mb: 1, maxWidth: 1100 }}>
          A † marks a coming week whose scheduled hours are under {Math.round(a.lowScheduleShare * 100)}% of the recent weeks' average ({Math.round(report.forecast.baseline.supplyHours)} h):
          time off, or shifts not entered yet. Read its gap with that in mind.
        </Alert>
      )}
      {report.warnings.includes('per_rep_unavailable') && (
        <Alert severity="warning" sx={{ mb: 1, maxWidth: 1100 }}>
          The Seniors' help and the team's CXHub work on 1A and AM could not be read just now, so the gap shows before help only. Reload to try again.
        </Alert>
      )}
      {report.warnings.includes('no_forecast_baseline') && (
        <Alert severity="warning" sx={{ mb: 1, maxWidth: 1100 }}>
          No recent week has a Support need, so there is no forecast.
        </Alert>
      )}
      {report.warnings.includes('bp_support_missing') && (
        <Alert severity="warning" sx={{ mb: 1, maxWidth: 1100 }}>
          Schedule Hub returned no Bright Pattern Support email, so the line is CXHub alone.
        </Alert>
      )}
      {report.warnings.includes('support_phone_unavailable') && (
        <Alert severity="warning" sx={{ mb: 1, maxWidth: 1100 }}>
          Schedule Hub could not be read for Support's phone queues just now, so the need below is the email alone. Reload to try again.
        </Alert>
      )}
      {report.warnings.includes('support_phone_missing') && (
        <Alert severity="warning" sx={{ mb: 1, maxWidth: 1100 }}>
          Schedule Hub returned no Support phone calls (TRQ Inbound Voice, CS Support), so the need below is the email alone.
        </Alert>
      )}
      {report.warnings.includes('cxhub_support_missing') && (
        <Alert severity="warning" sx={{ mb: 1, maxWidth: 1100 }}>
          No CXHub Support email was found for these weeks, so the line is Bright Pattern alone.
        </Alert>
      )}

      <Box sx={{ mt: 2, ...TABLE_BOX }}>
        <SectionHeading variant="subtitle2" sx={{ mb: 0.75 }} title="1. Volume" descriptionMaxWidth={1100}>
          CXHub counts the contacts that arrived in the week, spam out (the Weekly Report's valid count). Bright Pattern is the answered
          contacts on every CSS and TRQ email queue, not the outbound ones. Emails handled is the part of the total that took agent time
          (CXHub contacts a person handled, plus the Bright Pattern answered): the count the email FTE is worked from. The two phone columns are
          calls answered, kept apart from the email line: TRQ Inbound Voice is customers calling Support, and CS Support (with CS Tech Support) is
          agents calling Support for help on an order or a fitment question. The internal calls are Support's workload, not customer demand, so
          they are not in the Phone view's volume. This week is only so far.
        </SectionHeading>
        <VolumeTable report={report} />
        {report.forecast?.multipliers?.length > 0 && (
          <Box sx={{ mt: 1 }}>
            <SectionHeading variant="subtitle2" appearance="quiet" title="Holiday multipliers" descriptionMaxWidth={1100}>
              Each listed week's email work is the level times Used; any other week is 1.0. Used is the median of the three years beside it:
              Bright Pattern's answered contacts on the CSS and TRQ email queues that week against the mean of the weeks ending mid-September
              to late October, lined up by the holiday. The median is used because 2025 ran low while those queues were moving to CXHub. The
              Support email falls in Thanksgiving week and Christmas week every year, more than customer email in general. The phone work is not
              scaled. The numbers are in SUPPORT_PLAN (weekMultipliers) and are yours to change.
            </SectionHeading>
            <MultiplierTable rows={report.forecast.multipliers} />
          </Box>
        )}
      </Box>

      <Box sx={{ mt: 3, ...TABLE_BOX }}>
        <SectionHeading variant="subtitle2" sx={{ mb: 0.75 }} title="2. Staffing" descriptionMaxWidth={1100}>
          The work is the contacts a person handled times handle time. CXHub's handle time is the Reporting one for Support Email (claim to
          action, pauses out, spam out). Bright Pattern's is Schedule Hub's raw handle time divided by {a.bpInFocusDivisor}, because it counts time the
          agent was not looking at the email (Power BI's in-focus time for these queues ran about 1 over {a.bpInFocusDivisor} of it in October); that is an
          estimate. FTE needed is that work ÷ {a.onQueueHoursPerFte} hours × {1 + a.shrinkageRate}, your {Math.round(a.shrinkageRate * 100)}% standard with no flat spare.
          FTE scheduled is the supply: the {a.scheduleTeams.join(' and ')} team's whole scheduled hours ÷ {a.scheduledHoursPerFte}, in the CXHub blocks and the
          Bright Pattern queues alike, because the team answers both. The {a.helpTeams.join(' and ')} are not supply: they work wherever the business needs
          them, so the Support contacts they handled in CXHub (contacts × handle time) come off the need before the markup, and the gap is shown
          before and after that help (their Bright Pattern help is not measured per person). The two memo columns are what the team's schedule also
          covers: its Bright Pattern queue hours, and its own CXHub work on 1A and AM. A week with under 4 scheduled hours, or past the roster, has no
          supply or gap. Support's need also counts its phone work, the TRQ Inbound
          Voice and CS Support calls, at Schedule Hub's own handle time (phone has no out-of-focus time, so no divisor), on the same
          {a.onQueueHoursPerFte} hours × {1 + a.shrinkageRate}, and it is in the total need and the gap.
        </SectionHeading>
        <StaffingTable report={report} showHours={showHours} />
      </Box>

      <Box sx={{ mt: 3, ...TABLE_BOX }}>
        <SectionHeading variant="subtitle2" sx={{ mb: 0.75 }} title="3. Service" descriptionMaxWidth={1100}>
          Average time from a contact arriving to its first answer, in hours, from the Reporting ASA, against each target: Support Email 12 hours,
          and Walmart 24. Walmart is a subset of Support Email: its contacts are in Support Email's number and in the volume, handle time and
          staffing above, and its own ASA is shown beside it. Bright Pattern has no usable email service level (it always reads 100%), so its queues have none.
        </SectionHeading>
        <ServiceTable report={report} />
      </Box>

      <SectionHeading variant="subtitle2" appearance="quiet" sx={{ mt: 2 }} title="Colors and notes" descriptionMaxWidth={1100}>
        Colors: gap red below 0, amber under +1; speed of answer red over the target, amber within 20% of it. "—" means no data yet, never
        zero. Italic is this week so far, or a forecast row.
      </SectionHeading>
    </Box>
  )
}

export default function ForecastSupport() {
  const [report, setReport] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    let live = true
    fetchForecastSupport()
      .then((r) => { if (live) setReport(r) })
      .catch((e) => { if (live) setError(e.message) })
      .finally(() => { if (live) setLoading(false) })
    return () => { live = false }
  }, [])

  if (loading) return <Box sx={{ py: 2 }}><CircularProgress size={24} /></Box>
  if (error) return <Alert severity="error">{error}</Alert>
  if (!report) return null
  return <ForecastSupportView report={report} />
}
